/**
 * "Who can see what" (Ed, 2026-09-24) — a read-only table of which roles
 * see which planner views, for Users & Roles.
 *
 * GENERATED, never typed: each cell is the answer the planner's own gate
 * gives for that role (the same `canSee…` predicates the rail and the
 * router use), so the table cannot drift from what is enforced. Change a
 * predicate in claims.ts and this table changes with it.
 *
 * Deliberately not editable: hiding a view is one of three locks, and each
 * view's server call checks the role again in code. An editable screen
 * could widen a view the server still refuses, or, made to drive the
 * server too, turn permissions into data anyone with the screen can edit.
 */
import {
  canSeeAccessRequests,
  canSeeAuditLog,
  canSeeConnect,
  canSeeGoalsInventoryTab,
  canSeeSOMAPlansTab,
  canSeeSomaAdoption,
  canSeeUsersAndRoles,
  type AccessRole,
  type AuthClaims,
} from "./claims";

export interface ViewRule {
  view: string;
  /** What the view is for, in a phrase. */
  about: string;
  canSee: (claims: AuthClaims | null) => boolean;
}

/** In rail order. Chase is gated with Connect's predicate (navigation.ts
 *  and shell.ts), so it is listed with that predicate here too. */
export const VIEW_RULES: ViewRule[] = [
  { view: "Plans", about: "clients' care plans", canSee: canSeeSOMAPlansTab },
  { view: "Goals Inventory", about: "the catalogue plans are built from", canSee: canSeeGoalsInventoryTab },
  { view: "Connect", about: "clients' sensors", canSee: canSeeConnect },
  { view: "Chase", about: "owed DSRs and shift records", canSee: canSeeConnect },
  { view: "Adoption", about: "which planners and clients have started", canSee: canSeeSomaAdoption },
  { view: "Users & Roles", about: "who has access, and changing it", canSee: canSeeUsersAndRoles },
  { view: "Access requests", about: "family asking for app access", canSee: canSeeAccessRequests },
  { view: "Audit log", about: "every change, in order", canSee: canSeeAuditLog },
];

/** Most access first, as the role order elsewhere in Users & Roles. Carers
 *  are listed because "none" is an answer: the planner shows them nothing. */
export const ROLE_ORDER: AccessRole[] = [
  "admin", "planner", "librarian", "auditor", "practice-author", "carer",
];

function claimsFor(role: AccessRole): AuthClaims {
  // Every predicate reads the role only; the rest is a valid, inert shape.
  return { role, dataScope: "real", personDocId: "who-can-see-what", careTeamMemberships: [] };
}

/** view -> role -> can see. */
export function viewAccessMatrix(
  rules: ViewRule[] = VIEW_RULES,
  roles: AccessRole[] = ROLE_ORDER,
): { view: string; about: string; roles: Record<string, boolean> }[] {
  return rules.map((r) => ({
    view: r.view,
    about: r.about,
    roles: Object.fromEntries(roles.map((role) => [role, r.canSee(claimsFor(role))])),
  }));
}
